import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

import BookEquationView from './BookEquationView';
import {
  decodeEquationLatex,
  equationAnchorAttributes,
  normalizeBookEquation,
} from './bookEquationCodec';

const EQUATION_NODE_NAMES = new Set([
  'bookEquationInline',
  'bookEquationBlock',
]);

const parseEquationAttributes = (element, expectedDisplay) => {
  if (
    element.getAttribute('data-book-equation') !== 'true' ||
    element.getAttribute('data-equation-display') !== expectedDisplay
  ) {
    return false;
  }

  try {
    const latex = decodeEquationLatex(
      element.getAttribute('data-equation-latex')
    );
    return normalizeBookEquation({ latex, display: expectedDisplay });
  } catch {
    return false;
  }
};

const equationAttributes = (display) => ({
  latex: {
    default: '',
    renderHTML: () => ({}),
  },
  display: {
    default: display,
    renderHTML: () => ({}),
  },
});

const renderEquation = (node, expectedDisplay, HTMLAttributes) => {
  const normalized = normalizeBookEquation({
    latex: node.attrs.latex,
    display: expectedDisplay,
  });
  const tag = expectedDisplay === 'inline' ? 'span' : 'div';
  return [
    tag,
    mergeAttributes(
      HTMLAttributes,
      equationAnchorAttributes(normalized)
    ),
  ];
};

const equationOptions = () => ({
  onEditEquation: null,
});

export const BookEquationInline = Node.create({
  name: 'bookEquationInline',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addOptions: equationOptions,

  addAttributes() {
    return equationAttributes('inline');
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-book-equation="true"]',
        getAttrs: (element) => parseEquationAttributes(element, 'inline'),
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return renderEquation(node, 'inline', HTMLAttributes);
  },

  addNodeView() {
    return ReactNodeViewRenderer(BookEquationView);
  },

  addCommands() {
    return {
      insertBookEquation:
        (equation) =>
        ({ commands }) => {
          let normalized;
          try {
            normalized = normalizeBookEquation(equation);
          } catch {
            return false;
          }
          return commands.insertContent({
            type:
              normalized.display === 'inline'
                ? 'bookEquationInline'
                : 'bookEquationBlock',
            attrs: {
              latex: normalized.latex,
              display: normalized.display,
            },
          });
        },
      updateBookEquation:
        ({ pos, ...equation }) =>
        ({ state, commands }) => {
          let normalized;
          try {
            normalized = normalizeBookEquation(equation);
          } catch {
            return false;
          }
          const target = state.doc.nodeAt(pos);
          if (!target || !EQUATION_NODE_NAMES.has(target.type.name)) {
            return false;
          }
          return commands.insertContentAt(
            { from: pos, to: pos + target.nodeSize },
            {
              type:
                normalized.display === 'inline'
                  ? 'bookEquationInline'
                  : 'bookEquationBlock',
              attrs: {
                latex: normalized.latex,
                display: normalized.display,
              },
            }
          );
        },
      deleteBookEquation:
        ({ pos }) =>
        ({ state, commands }) => {
          const target = state.doc.nodeAt(pos);
          if (!target || !EQUATION_NODE_NAMES.has(target.type.name)) {
            return false;
          }
          return commands.deleteRange({
            from: pos,
            to: pos + target.nodeSize,
          });
        },
    };
  },
});

export const BookEquationBlock = Node.create({
  name: 'bookEquationBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addOptions: equationOptions,

  addAttributes() {
    return equationAttributes('block');
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-book-equation="true"]',
        getAttrs: (element) => parseEquationAttributes(element, 'block'),
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return renderEquation(node, 'block', HTMLAttributes);
  },

  addNodeView() {
    return ReactNodeViewRenderer(BookEquationView);
  },
});

export default BookEquationInline;
